import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { Button } from 'shadcn/components/ui/Button';
import { useBulkMovePalletsAsyncMutation } from 'shared/hooks/warehouse/useBulkMovePalletsAsyncMutation';
import {
    isRetriableBulkMovePollError,
    useBulkMovePalletsAsyncStatusQuery,
} from 'shared/hooks/warehouse/useBulkMovePalletsAsyncStatusQuery';

import { BulkMovePalletsSummary } from './BulkMovePalletsSummary';

const KICKOFF_ERROR_FALLBACK = 'Failed to move pallets. Please try again.';
const STATUS_ERROR_FALLBACK = 'This move batch was not found or has expired. Please try again.';

function getErrorMessage(error, fallback) {
    if (!error) {
        return null;
    }
    const message = typeof error.message === 'string' ? error.message.trim() : '';
    return message || fallback;
}

function BulkMovePalletsConfirmation({
    destinationLocationId,
    destinationLocationName,
    onCancel,
    onGoBack,
    onSuccess,
    selectedPalletIds,
}) {
    const numPallets = selectedPalletIds.length;
    const [batchId, setBatchId] = useState(null);

    const kickoffMutation = useBulkMovePalletsAsyncMutation();
    const statusQuery = useBulkMovePalletsAsyncStatusQuery({ batchId });

    const batchProgress = statusQuery.data;
    const isCompleted = batchProgress?.status === 'completed';
    // Ignore late status errors once completed, and hide retriable blips while we keep polling.
    const terminalStatusError =
        !isCompleted && statusQuery.isError && !isRetriableBulkMovePollError(statusQuery.error)
            ? statusQuery.error
            : null;
    const statusError = getErrorMessage(terminalStatusError, STATUS_ERROR_FALLBACK);
    const kickoffError = getErrorMessage(
        kickoffMutation.isError ? kickoffMutation.error : null,
        KICKOFF_ERROR_FALLBACK,
    );
    const error = kickoffError || statusError || null;
    const isKickoffPending = kickoffMutation.isPending;
    const hasStartedBatch = Boolean(batchId);
    const hasTerminalStatusError = hasStartedBatch && Boolean(statusError) && !batchProgress;

    const handleConfirmMove = () => {
        if (hasStartedBatch) {
            return;
        }
        kickoffMutation.reset();
        kickoffMutation.mutate(
            { palletIds: selectedPalletIds, destinationLocationId },
            {
                onSuccess: (data) => {
                    setBatchId(data.batchId);
                },
            },
        );
    };

    const handleClose = () => {
        const numSuccesses = batchProgress?.succeeded || 0;
        if (numSuccesses > 0) {
            onSuccess();
        } else {
            onCancel();
        }
    };

    if (batchProgress) {
        return <BulkMovePalletsSummary batchProgress={batchProgress} error={error} onClose={handleClose} />;
    }

    if (hasTerminalStatusError) {
        return (
            <div className="p-6 flex flex-col items-center gap-6">
                <p className="text-red-600 text-sm text-center">{error}</p>
                <Button variant="default" onClick={handleClose}>
                    Close
                </Button>
            </div>
        );
    }

    return (
        <div className="p-6 flex flex-col items-center gap-6">
            <p className="text-lg text-center">
                Are you sure you want to move {numPallets} pallet{numPallets !== 1 ? 's' : ''} to location{' '}
                {destinationLocationName}?
            </p>
            {error && <p className="text-red-600 text-sm text-center">{error}</p>}
            {hasStartedBatch && !error && <p className="text-sm text-muted-foreground text-center">Starting move…</p>}
            <div className="flex gap-4">
                <Button variant="default" onClick={handleConfirmMove} disabled={isKickoffPending || hasStartedBatch}>
                    {isKickoffPending ? (
                        <>
                            <svg
                                className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                            >
                                <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                />
                                <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                />
                            </svg>
                            Moving Pallets...
                        </>
                    ) : (
                        'Yes, Move Pallets'
                    )}
                </Button>
                <Button variant="secondary" onClick={onGoBack} disabled={isKickoffPending || hasStartedBatch}>
                    No, Go Back
                </Button>
            </div>
        </div>
    );
}

BulkMovePalletsConfirmation.propTypes = {
    destinationLocationId: PropTypes.string.isRequired,
    destinationLocationName: PropTypes.string.isRequired,
    onCancel: PropTypes.func.isRequired,
    onGoBack: PropTypes.func.isRequired,
    onSuccess: PropTypes.func.isRequired,
    selectedPalletIds: PropTypes.arrayOf(PropTypes.number).isRequired,
};

export { BulkMovePalletsConfirmation };
